<template>
  <section class="status-card" :class="submitted ? 'is-submitted' : 'is-progress'">
    <div class="status-icon">
      <PortalIcon :name="submitted ? 'check' : 'clock'" :size="17" />
    </div>
    <div class="status-body">
      <h2 class="status-title">{{ status.title }}</h2>
      <p class="status-text">{{ status.text }}</p>
    </div>
    <div v-if="!submitted" class="status-progress">
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>
      <span class="progress-label">{{ step }} de 4 pasos</span>
    </div>
    <button v-if="!submitted" type="button" class="solid-btn status-cta" @click="$emit('edit', step)">
      <span>{{ step === 3 ? "Firmar y enviar" : `Completar ${nextSection.title.toLowerCase()}` }}</span>
      <PortalIcon name="chevron-right" :size="16" />
    </button>
  </section>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import { SECTIONS, applicationStep } from "./candidateSummary";

// Where the application stands (in progress, or sent to Reclutamiento), with the button for its next step.
export default {
  name: "PortalStatusCard",
  components: { PortalIcon },
  props: {
    candidate: { type: Object, required: true },
  },
  computed: {
    step() {
      return applicationStep(this.candidate);
    },
    submitted() {
      return this.step >= 4;
    },
    nextSection() {
      return SECTIONS[Math.min(this.step, SECTIONS.length - 1)];
    },
    progress() {
      return Math.round((Math.min(this.step, 4) / 4) * 100);
    },
    status() {
      if (this.submitted) {
        return {
          title: "Solicitud enviada",
          text:
            "Su expediente está en manos de Reclutamiento. Le contactarán al correo indicado si su perfil es compatible con una vacante.",
        };
      }
      if (this.step === 0) {
        return {
          title: "Empecemos su solicitud",
          text: "Complete sus secciones y firme la declaración para enviarla. Cada una se guarda al terminar.",
        };
      }
      if (this.step === 3) {
        return {
          title: "Solo falta firmar",
          text: "Lea y firme la declaración jurada para enviar su solicitud.",
        };
      }
      return {
        title: "Su solicitud está en progreso",
        text: `Siguiente paso: ${this.nextSection.title}. Al terminar todas podrá firmar y enviar.`,
      };
    },
  },
};
</script>

<style scoped>
/* The portal's plain white card (as the credential and the section cards), kept compact: an icon chip beside the title,
   then the text, the progress and the button across the full width (it sits in the narrow side column). */
.status-card {
  /* Same stacking layer as the (positioned) credential card, so each card covers the shadow of the one above. */
  position: relative;
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  gap: 8px 12px;
  align-items: center;
  padding: 14px 16px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.05), 0 8px 24px -10px rgba(10, 16, 32, 0.14);
}
.status-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.is-progress .status-icon {
  background: #fff3dc;
  box-shadow: inset 0 0 0 1px #f1d9a8;
  color: #9a6a1c;
}
.is-submitted .status-icon {
  background: #eaf8f1;
  box-shadow: inset 0 0 0 1px #bfe8d3;
  color: #0f7a4f;
}
.is-progress .status-icon svg {
  stroke-width: 2.2;
}
.is-submitted .status-icon svg {
  stroke-width: 2.6;
}
/* Its title and text are laid out by the card's grid. */
.status-body {
  display: contents;
}
.status-title {
  grid-column: 2;
  margin: 0;
  font: 600 15px/1.35 "Geist", system-ui, sans-serif;
  color: #0e1424;
  text-wrap: balance;
}
.status-text {
  grid-column: 1 / -1;
  margin: 0;
  font: 400 13px/1.45 "Geist", system-ui, sans-serif;
  color: #6b7283;
  text-wrap: pretty;
}
.status-progress {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 12px;
}
.progress-track {
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: #eef0f4;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 999px;
  background: #e6b65c;
  transition: width 0.4s;
}
.progress-label {
  font: 500 12px "Geist Mono", ui-monospace, monospace;
  color: #8a91a3;
  white-space: nowrap;
}
.solid-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: #0e1424;
  box-shadow: 0 6px 14px -6px rgba(10, 16, 32, 0.6);
  color: #fff;
  font: 600 13.5px "Geist", system-ui, sans-serif;
  white-space: nowrap;
  cursor: pointer;
}
.solid-btn:hover {
  background: #24304d;
}
.status-cta {
  grid-column: 1 / -1;
  justify-content: center;
  height: 36px;
}
.status-cta svg {
  width: 14px;
  height: 14px;
  stroke-width: 2.6;
}
</style>
